import { useState, useMemo, useRef, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, Badge } from '@school-erp/ui';
import { NotificationBell } from '../components/NotificationBell';
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  UserCheck,
  BookOpen,
  CalendarCheck,
  ClipboardList,
  DollarSign,
  Briefcase,
  Package,
  Bell,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ShieldCheck,
  PenTool,
  Clock,
  BarChart3,
  UserCog,
  UserPlus,
  Library,
  Bus,
  DoorOpen,
  Building2,
  Heart,
  CalendarDays,
  Search,
  ChevronRight,
  PanelLeftClose,
  PanelLeft,
} from 'lucide-react';
import type { RoleFamily, Permission } from '@school-erp/types';

interface NavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  requiredPermissions?: Permission[];
  requiredRoles?: RoleFamily[];
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

// ─── All navigation items ───
const allItems: Record<string, NavItem> = {
  dashboard: { key: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} />, path: '/' },
  students: {
    key: 'students',
    label: 'Students',
    icon: <GraduationCap size={18} />,
    path: '/students',
    requiredPermissions: ['students.read'],
  },
  admissions: {
    key: 'admissions',
    label: 'Admissions',
    icon: <UserPlus size={18} />,
    path: '/students/new',
    requiredPermissions: ['students.write'],
  },
  guardians: {
    key: 'guardians',
    label: 'Guardians',
    icon: <ShieldCheck size={18} />,
    path: '/guardians',
    requiredPermissions: ['guardians.read'],
  },
  users: {
    key: 'users',
    label: 'Users & Roles',
    icon: <Users size={18} />,
    path: '/users',
    requiredPermissions: ['users.read'],
  },
  teachers: {
    key: 'teachers',
    label: 'Teachers',
    icon: <UserCog size={18} />,
    path: '/teachers',
    requiredPermissions: ['employees.read'],
  },
  employees: {
    key: 'employees',
    label: 'Employees',
    icon: <UserCheck size={18} />,
    path: '/employees',
    requiredPermissions: ['employees.read'],
  },
  academics: {
    key: 'academics',
    label: 'Academic Setup',
    icon: <BookOpen size={18} />,
    path: '/academics',
    requiredPermissions: ['academics.read'],
  },
  timetable: {
    key: 'timetable',
    label: 'Timetable',
    icon: <Clock size={18} />,
    path: '/timetable',
    requiredPermissions: ['academics.read'],
  },
  attendance: {
    key: 'attendance',
    label: 'Attendance',
    icon: <CalendarCheck size={18} />,
    path: '/attendance',
    requiredPermissions: ['attendance.read'],
  },
  assignments: {
    key: 'assignments',
    label: 'Assignments',
    icon: <PenTool size={18} />,
    path: '/assignments',
    requiredPermissions: ['assignments.read'],
  },
  exams: {
    key: 'exams',
    label: 'Exams & Results',
    icon: <ClipboardList size={18} />,
    path: '/exams',
    requiredPermissions: ['exams.read'],
  },
  fees: {
    key: 'fees',
    label: 'Fees & Finance',
    icon: <DollarSign size={18} />,
    path: '/fees',
    requiredPermissions: ['fees.read'],
  },
  hr: {
    key: 'hr',
    label: 'HR & Payroll',
    icon: <Briefcase size={18} />,
    path: '/hr',
    requiredPermissions: ['hr.read'],
  },
  inventory: {
    key: 'inventory',
    label: 'Inventory',
    icon: <Package size={18} />,
    path: '/inventory',
    requiredPermissions: ['inventory.read'],
  },
  communication: {
    key: 'communication',
    label: 'Announcements',
    icon: <Bell size={18} />,
    path: '/communication',
    requiredPermissions: ['communication.read'],
  },
  messages: {
    key: 'messages',
    label: 'Messages',
    icon: <Bell size={18} />,
    path: '/communication',
    requiredPermissions: ['communication.read'],
  },
  reports: {
    key: 'reports',
    label: 'Reports',
    icon: <BarChart3 size={18} />,
    path: '/reports',
    requiredPermissions: ['reports.read'],
  },
  audit: {
    key: 'audit',
    label: 'Audit Log',
    icon: <FileText size={18} />,
    path: '/audit',
    requiredPermissions: ['audit.read'],
  },
  settings: {
    key: 'settings',
    label: 'Settings',
    icon: <Settings size={18} />,
    path: '/settings',
    requiredRoles: ['super_admin', 'school_admin'],
  },
  profile: { key: 'profile', label: 'Profile', icon: <Users size={18} />, path: '/profile' },
  myClasses: {
    key: 'myClasses',
    label: 'My Classes',
    icon: <BookOpen size={18} />,
    path: '/my-classes',
    requiredRoles: ['teacher'],
  },
  myWards: {
    key: 'myWards',
    label: 'My Wards',
    icon: <GraduationCap size={18} />,
    path: '/my-wards',
    requiredRoles: ['guardian'],
  },
  materials: {
    key: 'materials',
    label: 'Materials',
    icon: <FileText size={18} />,
    path: '/materials',
    requiredPermissions: ['assignments.read'],
  },
  notices: {
    key: 'notices',
    label: 'Notices',
    icon: <Bell size={18} />,
    path: '/communication',
    requiredPermissions: ['communication.read'],
  },
  results: {
    key: 'results',
    label: 'Results',
    icon: <ClipboardList size={18} />,
    path: '/exams',
    requiredPermissions: ['exams.read'],
  },
  library: {
    key: 'library',
    label: 'Library',
    icon: <Library size={18} />,
    path: '/library',
    requiredPermissions: ['library.read'],
  },
  transport: {
    key: 'transport',
    label: 'Transport',
    icon: <Bus size={18} />,
    path: '/transport',
    requiredPermissions: ['transport.read'],
  },
  frontOffice: {
    key: 'frontOffice',
    label: 'Front Office',
    icon: <DoorOpen size={18} />,
    path: '/front-office',
    requiredPermissions: ['front_office.read'],
  },
  hostel: {
    key: 'hostel',
    label: 'Hostel & Mess',
    icon: <Building2 size={18} />,
    path: '/hostel',
    requiredPermissions: ['hostel.read'],
  },
  infirmary: {
    key: 'infirmary',
    label: 'Infirmary',
    icon: <Heart size={18} />,
    path: '/infirmary',
    requiredPermissions: ['infirmary.read'],
  },
  events: {
    key: 'events',
    label: 'Events',
    icon: <CalendarDays size={18} />,
    path: '/events',
    requiredPermissions: ['events.read'],
  },
  myHr: { key: 'myHr', label: 'My HR', icon: <Briefcase size={18} />, path: '/hr', requiredPermissions: ['hr.read'] },
};

// ─── Breadcrumb label map ───
const breadcrumbLabels: Record<string, string> = {
  '': 'Dashboard',
  students: 'Students',
  guardians: 'Guardians',
  users: 'Users & Roles',
  teachers: 'Teachers',
  employees: 'Employees',
  academics: 'Academic Setup',
  timetable: 'Timetable',
  attendance: 'Attendance',
  assignments: 'Assignments',
  exams: 'Exams & Results',
  fees: 'Fees & Finance',
  hr: 'HR & Payroll',
  inventory: 'Inventory',
  communication: 'Announcements',
  reports: 'Reports',
  audit: 'Audit Log',
  settings: 'Settings',
  profile: 'Profile',
  'my-classes': 'My Classes',
  'my-wards': 'My Wards',
  materials: 'Materials',
  library: 'Library',
  transport: 'Transport',
  'front-office': 'Front Office',
  hostel: 'Hostel & Mess',
  infirmary: 'Infirmary',
  events: 'Events',
  new: 'New',
};

// ─── Role-specific grouped layouts (per PRD 06_UI_NAVIGATION_AND_WORKFLOW_SPEC) ───
function getNavGroupsForRole(primaryRole: RoleFamily): NavGroup[] {
  switch (primaryRole) {
    case 'super_admin':
    case 'school_admin':
      return [
        { label: '', items: [allItems.dashboard] },
        { label: 'STUDENT MANAGEMENT', items: [allItems.admissions, allItems.students, allItems.guardians] },
        { label: 'USER MANAGEMENT', items: [allItems.users] },
        { label: 'STAFF', items: [allItems.teachers, allItems.employees] },
        {
          label: 'ACADEMICS',
          items: [allItems.academics, allItems.timetable, allItems.attendance, allItems.assignments, allItems.exams],
        },
        { label: 'FINANCE & OPS', items: [allItems.fees, allItems.hr, allItems.inventory] },
        {
          label: 'FACILITIES',
          items: [allItems.library, allItems.transport, allItems.hostel, allItems.infirmary, allItems.frontOffice],
        },
        {
          label: 'MORE',
          items: [allItems.events, allItems.communication, allItems.reports, allItems.audit, allItems.settings],
        },
      ];
    case 'teacher':
      return [
        { label: '', items: [allItems.dashboard] },
        {
          label: 'TEACHING',
          items: [
            allItems.myClasses,
            allItems.timetable,
            allItems.attendance,
            allItems.assignments,
            allItems.materials,
            allItems.exams,
          ],
        },
        {
          label: 'RESOURCES',
          items: [allItems.students, allItems.library, allItems.messages, allItems.events, allItems.reports],
        },
        { label: 'SELF-SERVICE', items: [allItems.myHr] },
        { label: 'ACCOUNT', items: [allItems.profile] },
      ];
    case 'guardian':
      return [
        { label: '', items: [allItems.dashboard] },
        { label: 'MY WARDS', items: [allItems.myWards, allItems.attendance, allItems.assignments, allItems.results] },
        { label: 'FINANCE', items: [allItems.fees] },
        {
          label: 'MORE',
          items: [allItems.transport, allItems.events, allItems.notices, allItems.messages, allItems.profile],
        },
      ];
    case 'student':
      return [
        { label: '', items: [allItems.dashboard] },
        {
          label: 'ACADEMICS',
          items: [allItems.timetable, allItems.assignments, allItems.materials, allItems.attendance, allItems.results],
        },
        {
          label: 'MORE',
          items: [
            allItems.fees,
            allItems.library,
            allItems.transport,
            allItems.infirmary,
            allItems.events,
            allItems.notices,
            allItems.profile,
          ],
        },
      ];
    case 'hr_manager':
      return [
        { label: '', items: [allItems.dashboard] },
        { label: 'HR', items: [allItems.employees, allItems.attendance, allItems.hr] },
        { label: 'MORE', items: [allItems.reports, allItems.profile] },
      ];
    case 'store_manager':
      return [
        { label: '', items: [allItems.dashboard] },
        { label: 'INVENTORY', items: [allItems.inventory] },
        { label: 'MORE', items: [allItems.reports, allItems.profile] },
      ];
    case 'accountant':
      return [
        { label: '', items: [allItems.dashboard] },
        { label: 'FINANCE', items: [allItems.fees] },
        { label: 'MORE', items: [allItems.reports, allItems.profile] },
      ];
    case 'librarian':
      return [
        { label: '', items: [allItems.dashboard] },
        { label: 'LIBRARY', items: [allItems.library] },
        { label: 'MORE', items: [allItems.events, allItems.reports, allItems.profile] },
      ];
    default:
      return [{ label: '', items: [allItems.dashboard, allItems.profile] }];
  }
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  // Close profile dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    }
    if (profileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [profileOpen]);

  // Determine primary role for nav grouping
  const primaryRole: RoleFamily = user?.roles?.[0] ?? 'student';
  const navGroups = getNavGroupsForRole(primaryRole);

  // Filter items based on actual permissions
  const filteredGroups = navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (!user) return false;
        if (item.requiredRoles) {
          return user.roles.some((r) => item.requiredRoles!.includes(r));
        }
        if (item.requiredPermissions) {
          return item.requiredPermissions.some((p) => user.permissions.includes(p));
        }
        return true;
      }),
    }))
    .filter((group) => group.items.length > 0);

  // Flatten for active detection
  const allFilteredItems = filteredGroups.flatMap((g) => g.items);
  const activeKey =
    allFilteredItems.find((i) => i.path !== '/' && location.pathname.startsWith(i.path))?.key ??
    (location.pathname === '/' ? 'dashboard' : '');

  // Breadcrumbs from route
  const breadcrumbs = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length === 0) return [];
    const crumbs: Array<{ label: string; path: string }> = [];
    let path = '';
    for (const seg of segments) {
      path += `/${seg}`;
      crumbs.push({
        label: breadcrumbLabels[seg] ?? seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, ' '),
        path,
      });
    }
    return crumbs;
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const sidebarWidth = sidebarCollapsed ? 'w-[72px]' : 'w-[250px]';

  return (
    <div className="flex h-screen overflow-hidden bg-[#f8f9fa]">
      {/* ── Mobile overlay ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 ${sidebarWidth} bg-white border-r border-gray-200 transform transition-all duration-300 ease-in-out lg:static lg:translate-x-0 flex flex-col ${
          sidebarOpen ? 'translate-x-0 shadow-xl' : '-translate-x-full'
        }`}
      >
        {/* Logo area */}
        <div className="flex-shrink-0 flex items-center justify-between h-[60px] px-5 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 bg-[#3D5EE1] rounded-lg flex items-center justify-center flex-shrink-0">
              <GraduationCap size={18} className="text-white" />
            </div>
            {!sidebarCollapsed && (
              <span className="text-[17px] font-bold text-gray-900 tracking-tight whitespace-nowrap">SchoolERP</span>
            )}
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 overflow-y-auto py-4 sidebar-scroll">
          {filteredGroups.map((group, gi) => (
            <div key={gi} className={gi > 0 ? 'mt-4' : ''}>
              {/* Group header */}
              {group.label && !sidebarCollapsed && (
                <p className="px-6 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400 select-none">
                  {group.label}
                </p>
              )}
              {group.label && sidebarCollapsed && <div className="mx-auto w-8 border-t border-gray-100 my-2" />}

              {/* Nav items */}
              <ul>
                {group.items.map((item) => {
                  const isActive = activeKey === item.key;
                  return (
                    <li key={item.key}>
                      <button
                        onClick={() => {
                          navigate(item.path);
                          setSidebarOpen(false);
                        }}
                        title={sidebarCollapsed ? item.label : undefined}
                        className={`
                          relative w-full flex items-center gap-3 text-[13px] font-medium transition-colors duration-150
                          ${sidebarCollapsed ? 'justify-center px-2 py-2.5 mx-2 rounded-lg' : 'px-6 py-2.5'}
                          ${
                            isActive
                              ? 'bg-[#3D5EE1]/[0.08] text-[#3D5EE1]'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }
                        `}
                      >
                        {/* Left border accent for active state */}
                        {isActive && !sidebarCollapsed && (
                          <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#3D5EE1] rounded-r" />
                        )}
                        <span className={`flex-shrink-0 ${isActive ? 'text-[#3D5EE1]' : 'text-gray-400'}`}>
                          {item.icon}
                        </span>
                        {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Sidebar footer */}
        {!sidebarCollapsed && (
          <div className="flex-shrink-0 border-t border-gray-100 p-4">
            <div className="flex items-center gap-3 px-2">
              <Avatar name={user?.fullName ?? 'User'} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate leading-tight">{user?.fullName}</p>
                <p className="text-[11px] text-gray-400 capitalize truncate leading-tight mt-0.5">
                  {user?.roles?.[0]?.replace(/_/g, ' ')}
                </p>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* ── Main wrapper ── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* ── Top header bar ── */}
        <header className="flex-shrink-0 bg-white border-b border-gray-200 h-[60px] flex items-center px-4 sm:px-6 z-30">
          {/* Left side: hamburger + collapse toggle + breadcrumbs */}
          <div className="flex items-center gap-2 min-w-0">
            {/* Mobile hamburger */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 -ml-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Open sidebar"
            >
              <Menu size={20} />
            </button>

            {/* Desktop collapse toggle */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:flex p-2 -ml-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
            </button>

            {/* Breadcrumbs */}
            <nav className="hidden sm:flex items-center gap-1.5 text-sm ml-1 min-w-0">
              <button
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-[#3D5EE1] transition-colors flex-shrink-0"
              >
                <LayoutDashboard size={15} />
              </button>
              {breadcrumbs.map((crumb, i) => (
                <span key={i} className="flex items-center gap-1.5 min-w-0">
                  <ChevronRight size={13} className="text-gray-300 flex-shrink-0" />
                  {i === breadcrumbs.length - 1 ? (
                    <span className="text-gray-800 font-medium truncate">{crumb.label}</span>
                  ) : (
                    <button
                      onClick={() => navigate(crumb.path)}
                      className="text-gray-400 hover:text-[#3D5EE1] transition-colors truncate"
                    >
                      {crumb.label}
                    </button>
                  )}
                </span>
              ))}
            </nav>
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right side actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Search */}
            <button
              onClick={() => navigate('/search')}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Search"
            >
              <Search size={18} />
            </button>

            {/* Notifications */}
            <NotificationBell />

            {/* Divider */}
            <div className="hidden sm:block h-6 w-px bg-gray-200 mx-1" />

            {/* User profile dropdown */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2 pl-1 pr-1 sm:pr-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Avatar name={user?.fullName ?? 'User'} />
                <div className="hidden sm:block text-left leading-tight">
                  <p className="text-sm font-medium text-gray-800 truncate max-w-[120px]">{user?.fullName}</p>
                  <p className="text-[11px] text-gray-400 capitalize truncate max-w-[120px]">
                    {user?.roles?.[0]?.replace(/_/g, ' ')}
                  </p>
                </div>
                <ChevronDown
                  size={14}
                  className={`text-gray-400 transition-transform duration-200 hidden sm:block ${
                    profileOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Dropdown menu */}
              {profileOpen && (
                <div className="absolute right-0 z-50 mt-2 w-60 bg-white rounded-lg shadow-lg border border-gray-200 py-1 animate-fade-in">
                  {/* User info */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <Avatar name={user?.fullName ?? 'User'} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">{user?.fullName}</p>
                        <p className="text-xs text-gray-500 truncate">{user?.username}</p>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {user?.roles.map((r) => (
                        <Badge key={r} variant="info">
                          {r.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Menu items */}
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setProfileOpen(false);
                        navigate('/profile');
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <Users size={16} className="text-gray-400" />
                      My Profile
                    </button>
                    <button
                      onClick={() => {
                        setProfileOpen(false);
                        navigate('/settings');
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <Settings size={16} className="text-gray-400" />
                      Settings
                    </button>
                  </div>

                  {/* Logout */}
                  <div className="border-t border-gray-100 py-1">
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                    >
                      <LogOut size={16} />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ── Page content area ── */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
